//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  Product,
  SpatialTag,
  UiaProductTagRequest,
} from '@models/IItemMetadata';
import { ProductValidationErrors } from '@uia/products/constants/UiaProductConstants';
import { isDefined } from '@utils/filterPredicates';

export const validateProducts = (newProducts: UiaProductTagRequest[]) => {
  if (hasMissingProductImageOrName(newProducts)) {
    return ProductValidationErrors.MissingImageOrName;
  } else if (hasInvalidProductUrl(newProducts)) {
    return ProductValidationErrors.InvalidUrl;
  } else {
    return undefined;
  }
};

export const updateProductPositionsAndLabels = (
  newProducts: UiaProductTagRequest[],
): UiaProductTagRequest[] => {
  const sorted = sortProducts(newProducts, 'spatialTag');

  return sorted.map((product, idx) => {
    const position = idx + 1;

    return {
      ...product,
      position,
      spatialTag: product.spatialTag
        ? { ...product.spatialTag, label: position.toString() }
        : product.spatialTag,
    };
  });
};

export const hasMissingProductImageOrName = (
  newProducts: UiaProductTagRequest[],
) => {
  return newProducts.some((product) => {
    return !product.imageUrl || !product.name;
  });
};

export const hasInvalidProductUrl = (newProducts: UiaProductTagRequest[]) => {
  return newProducts.some((product) => {
    const url = product.destinationUrl;

    return !isDefined(url) || url.trim().length === 0;
  });
};

interface SortableItem {
  spatialTag?: SpatialTag;
  position?: number;
}

export const sortProducts = <T extends SortableItem>(
  productsList: T[],
  sortBy: 'spatialTag' | 'position',
): T[] => {
  return [...productsList].sort((a, b) => {
    if (!a[sortBy] && !b[sortBy]) {
      return 0;
    }
    if (!a[sortBy]) {
      return 1;
    }
    if (!b[sortBy]) {
      return -1;
    }
    if (a[sortBy] < b[sortBy]) {
      return -1;
    }
    if (a[sortBy] > b[sortBy]) {
      return 1;
    }
    return 0;
  });
};

export const formatProducts = (products: UiaProductTagRequest[]) => {
  const updatedProductPositions = updateProductPositionsAndLabels(products);
  return sortProducts(updatedProductPositions, 'position');
};

export const convertProducts = (
  products: Product[],
): UiaProductTagRequest[] => {
  return products.map((p) => ({
    ...p,
    productStyleId: p.productStyleId ?? '',
  }));
};
